import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import Header from "../../components/Header";
import DeleteModal from "../../components/DeleteModal";
import EquipmentTable from "./components/EquipmentTable";
import AddButton from "../../components/AddButton";
import Pagination from "../../components/Pagination";
import { AppDispatch, RootState } from "../../redux/store";
import {
  fetchEquipments,
  deleteEquipmentAsync,
} from "../../redux/slices/equipmentSlice";
import Loading from "../../components/Loading";
import ErrorMessage from "../../components/ErrorMessage";
import SuccessMessage from "../../components/SuccessMessage";
import Error from "../../components/Error";

const Equipments: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    items: equipmentData,
    loading,
    error,
    currentPage,
    totalPages,
  } = useSelector((state: RootState) => state.equipment);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [equipmentToDelete, setEquipmentToDelete] = useState<any>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchEquipments({ page: currentPage, limit: 10 }));
  }, [dispatch, currentPage]);

  const handlePageChange = (newPage: number) => {
    dispatch(fetchEquipments({ page: newPage, limit: 10 }));
  };

  const openEditPage = (equipment: any) => {
    navigate("/edit-equipment", { state: { equipment } });
  };

  const openDeleteModal = (equipment: any) => {
    setEquipmentToDelete(equipment);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setEquipmentToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (equipmentToDelete) {
        await dispatch(deleteEquipmentAsync(equipmentToDelete.id)).unwrap();
        setSuccessMessage("Equipment deleted successfully!");
        closeDeleteModal();
        dispatch(fetchEquipments({ page: currentPage, limit: 10 }));
      }
    } catch {
      setErrorMessage("Failed to delete equipment. Please try again.");
      closeDeleteModal();
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <Header title="Equipment" />
      {loading ? (
        <Loading message="Loading equipment..." />
      ) : error ? (
        <Error
          message="Failed to fetch equipments"
          onRetry={() =>
            dispatch(fetchEquipments({ page: currentPage, limit: 10 }))
          }
        />
      ) : (
        <>
          <div className="mt-4 flex justify-end">
            <AddButton
              onClick={() => navigate("/add-equipment")}
              text="Add Equipment"
            />
          </div>
          <div className="overflow-x-auto">
            {successMessage && (
              <SuccessMessage
                message={successMessage}
                onClose={() => setSuccessMessage(null)}
              />
            )}
            {errorMessage && (
              <ErrorMessage
                message={errorMessage}
                onClose={() => setErrorMessage(null)}
              />
            )}
            <EquipmentTable
              equipment={equipmentData}
              onEdit={openEditPage}
              onDelete={openDeleteModal}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        itemName={equipmentToDelete?.name}
        itemType="equipment"
      />
    </div>
  );
};

export default Equipments;
